import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { getNextElementOnKey } from './helpers/menuHelpers.js';
import { MenuEvent } from './enums/menu-event.js';
export { MenuEvent };
let ViedocMenu = class ViedocMenu extends LitElement {
    constructor() {
        super(...arguments);
        this.disabled = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('role', 'listbox');
        this.setAttribute('aria-disabled', `${this.disabled}`);
    }
    _getMenuItems() {
        return [...this._defaultSlot.assignedElements({ flatten: true })].filter(el => el.getAttribute('role') === 'option');
    }
    getCurrentItem() {
        const currentItem = this._getMenuItems().find(item => item.getAttribute('tabindex') === '0');
        return currentItem || this._getMenuItems()[0];
    }
    setCurrentItem(item) {
        this._getMenuItems().forEach(i => {
            // eslint-disable-next-line no-param-reassign
            i.tabIndex = i === item ? (i.tabIndex = 0) : -1;
        });
    }
    _handleKeyDown(event) {
        const { code } = event;
        if (code === 'Enter') {
            const currentItem = this.getCurrentItem();
            currentItem === null || currentItem === void 0 ? void 0 : currentItem.click(); // simulate click event to select item;
        }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(code)) {
            event.preventDefault();
            const items = this._getMenuItems();
            const currentItem = this.getCurrentItem();
            const nextItem = getNextElementOnKey(code, items, currentItem);
            if (nextItem) {
                this.setCurrentItem(nextItem);
                nextItem.focus();
            }
        }
    }
    // Set focus and tabindex 0 on item that is hovered
    _handleMouseOver(event) {
        const target = event.target;
        const item = target.closest('viedoc-menu-item');
        this.setCurrentItem(item);
        item.focus();
    }
    _handleClick(event) {
        const target = event.target;
        const item = target.closest('viedoc-menu-item');
        if ((item === null || item === void 0 ? void 0 : item.disabled) === false) {
            const customEvent = new CustomEvent(MenuEvent.Select, {
                bubbles: true,
                cancelable: false,
                composed: true,
                detail: { item },
            });
            this.dispatchEvent(customEvent);
        }
    }
    handleSlotChange() {
        const items = this._getMenuItems();
        if (items.length > 0) {
            this.setCurrentItem(items[0]);
        }
    }
    render() {
        const classNames = classMap({
            menu: true,
        });
        /* eslint-disable lit-a11y/mouse-events-have-key-events */
        /* Instead of listening to focus event
            focus is set manually when hovering */
        return html `
      <div class=${classNames}>
        <div class="menu__scroll-container">
          <slot
            @click=${this._handleClick}
            @mouseover=${this._handleMouseOver}
            @keydown=${this._handleKeyDown}
            @slotchange=${this.handleSlotChange}
          ></slot>
        </div>
      </div>
    `;
    }
};
ViedocMenu.styles = [
    css `
      .menu {
        box-sizing: border-box;
        display: block;
        width: 100%;
        max-height: var(--viedoc-menu-max-height, none);
        border-radius: 8px;
        box-shadow: 0px 4px 14px rgb(0 0 0 / 25%);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        isolation: isolate; // Fixes issue with safari not respecting the border radius with overflow hidden
      }

      .menu__scroll-container {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        gap: 1px;
        background-color: var(--color-gray-15);
      }

      ::slotted(viedoc-menu-item) {
        background-color: var(--secondary-background-color);
      }
    `,
];
__decorate([
    property({ type: Boolean })
], ViedocMenu.prototype, "disabled", void 0);
__decorate([
    query('slot')
], ViedocMenu.prototype, "_defaultSlot", void 0);
ViedocMenu = __decorate([
    customElement('viedoc-menu')
], ViedocMenu);
export { ViedocMenu };
